import React, { useContext } from 'react'
import { useHistory } from 'react-router'

import { routes } from 'constants/routes'

import {
  useCreateWorkspaceProjectMutation,
  useRenameProjectMutation,
  GetProjectsDocument,
  CreateWorkspaceProjectMutation,
  GetProjectsQueryVariables,
} from 'gqlTypes'

import ToastContext from 'containers/ToastManager/ToastContext'

import { InlineEditorForm } from './InlineEditorForm'

const typeInline = <T extends any>(variables: T) => variables

interface CreateInlineEditorProps {
  workspaceId: string
  placeholder?: string
  toggleEditor: () => void
  onCompleted?: () => void
}

interface RenameInlineEditorProps {
  projectName: string
  projectId: string
  workspaceId: string
  placeholder: string
  toggleEditor: () => void
  onCompleted?: () => void
}

interface FormValues {
  projectName: string
}

const CreateInlineEditor = ({
  workspaceId,
  placeholder = 'New project...',
  toggleEditor,
  onCompleted,
}: CreateInlineEditorProps) => {
  const { showToast } = useContext(ToastContext)
  const history = useHistory()

  const handleOnComplete = (data: CreateWorkspaceProjectMutation) => {
    const { name, shortId: projectId } = data.createWorkspaceProject.project

    showToast(`"${name}" Project created`)

    onCompleted && onCompleted()

    toggleEditor()

    history.push(
      routes.WORKSPACE_PROJECT.create({
        projectId,
        workspaceId,
      })
    )
  }

  // NOTE: At the moment we are not paginating projects
  // and after https://github.com/sketch-hq/cloud-frontend/pull/3298
  // if we ever want to paginate them we might need to re-think the project list
  const [createWorkspaceProject] = useCreateWorkspaceProjectMutation({
    onCompleted: handleOnComplete,
    onError: 'show-toast',
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: { workspaceId },
      },
    ],
  })

  const onSubmit = ({ projectName }: FormValues) => {
    return createWorkspaceProject({
      variables: {
        projectName,
        workspaceId,
      },
    })
  }

  return (
    <InlineEditorForm
      placeholder={placeholder}
      onSubmit={onSubmit}
      toggleEditor={toggleEditor}
    />
  )
}

const RenameInlineEditor = ({
  projectName,
  projectId,
  workspaceId,
  placeholder,
  toggleEditor,
  onCompleted,
}: RenameInlineEditorProps) => {
  const { showToast } = useContext(ToastContext)

  const [renameProject] = useRenameProjectMutation({
    redirectErrors: true,
    onCompleted: () => {
      showToast('Project was renamed successfully')
      toggleEditor()
      onCompleted?.()
    },
    onError: 'show-toast',
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
  })

  const onSubmit = ({ projectName: newProjectName }: FormValues) => {
    if (projectName === newProjectName) {
      toggleEditor()
      return
    }

    return renameProject({
      variables: {
        name: newProjectName,
        projectId,
      },
    })
  }

  return (
    <InlineEditorForm
      placeholder={placeholder}
      onSubmit={onSubmit}
      toggleEditor={toggleEditor}
      defaultValue={projectName}
    />
  )
}

export { CreateInlineEditor, RenameInlineEditor }
